import { isTaskResultStatusSuccessLike, resolveTaskResultDeliverySemantic, } from "@wakeloop/agent-runtime";
import { getCompensationDelayMs } from "./recovery-policy.js";
export const RUNTIME_INFLIGHT_TASK_TRANSITION_MATRIX = {
    trackNewInflightTask: {
        kind: "track_new_inflight_task",
        category: "track",
        allowedFromStates: ["absent"],
        toState: "tracked",
    },
    refreshTrackedInflightTask: {
        kind: "refresh_tracked_inflight_task",
        category: "track",
        allowedFromStates: ["tracked"],
        toState: "tracked",
    },
    settleTrackedInflightTask: {
        kind: "settle_tracked_inflight_task",
        category: "track",
        allowedFromStates: ["tracked"],
        toState: "absent",
    },
};
export const RUNTIME_REPLAY_TRANSITION_MATRIX = {
    surfaceReplayCandidate: {
        kind: "surface_replay_candidate",
        category: "recovery",
        allowedFromStates: ["idle"],
        toState: "candidate",
    },
    commitRecoveryReplay: {
        kind: "commit_recovery_replay",
        category: "recovery",
        allowedFromStates: ["candidate"],
        toState: "committed",
    },
    settleCommittedReplay: {
        kind: "settle_committed_replay",
        category: "recovery",
        allowedFromStates: ["committed"],
        toState: "settled",
    },
};
export const RUNTIME_TASK_RESULT_TRANSITION_MATRIX = {
    emitSuccessPayload: {
        kind: "emit_success_payload",
        category: "result",
        allowedFromStates: ["pending_result"],
        toState: "success",
    },
    emitPartialSuccessPayload: {
        kind: "emit_partial_success_payload",
        category: "result",
        allowedFromStates: ["pending_result"],
        toState: "partial_success",
    },
    emitFailedPayload: {
        kind: "emit_failed_payload",
        category: "result",
        allowedFromStates: ["pending_result"],
        toState: "failed",
    },
};
export const RUNTIME_COMPENSATION_TRANSITION_MATRIX = {
    scheduleNextCompensation: {
        kind: "schedule_next_compensation",
        category: "compensation",
        allowedFromStates: ["active"],
        toState: "active",
        terminal: false,
    },
    settleSuccessfulCompensation: {
        kind: "settle_successful_compensation",
        category: "compensation",
        allowedFromStates: ["active"],
        toState: "terminal",
        terminal: true,
    },
    exhaustFailedCompensation: {
        kind: "exhaust_failed_compensation",
        category: "compensation",
        allowedFromStates: ["active"],
        toState: "terminal",
        terminal: true,
    },
};
const validateTransition = (transition, fromState, label) => {
    if (!transition.allowedFromStates.includes(fromState)) {
        throw new Error(`${label} ${transition.kind} does not allow ${String(fromState)}`);
    }
    return transition;
};
export const resolveInflightTaskLifecycleState = (input) => input.hasInflightTask ? "tracked" : "absent";
export const resolveReplayLifecycleState = (input) => {
    if (input.hasInflightTask && input.replayCommitted) {
        return "committed";
    }
    if (input.hasInflightTask) {
        return "candidate";
    }
    if (input.replayCommitted) {
        return "settled";
    }
    return "idle";
};
export const resolveInflightTaskTransitionForTracking = (input) => validateTransition(input.hasInflightTask
    ? RUNTIME_INFLIGHT_TASK_TRANSITION_MATRIX.refreshTrackedInflightTask
    : RUNTIME_INFLIGHT_TASK_TRANSITION_MATRIX.trackNewInflightTask, resolveInflightTaskLifecycleState({
    hasInflightTask: input.hasInflightTask,
}), "runtime inflight transition");
export const resolveInflightTaskTransitionForSettle = (input) => validateTransition(RUNTIME_INFLIGHT_TASK_TRANSITION_MATRIX.settleTrackedInflightTask, resolveInflightTaskLifecycleState({
    hasInflightTask: input.hasInflightTask,
}), "runtime inflight transition");
export const resolveReplayTransitionForPlan = (input) => {
    if (!input.hasReplayCandidates) {
        return null;
    }
    return validateTransition(RUNTIME_REPLAY_TRANSITION_MATRIX.surfaceReplayCandidate, "idle", "runtime replay transition");
};
export const resolveReplayTransitionForCommit = (input) => validateTransition(RUNTIME_REPLAY_TRANSITION_MATRIX.commitRecoveryReplay, resolveReplayLifecycleState(input), "runtime replay transition");
export const resolveReplayTransitionForSettle = (input) => validateTransition(RUNTIME_REPLAY_TRANSITION_MATRIX.settleCommittedReplay, resolveReplayLifecycleState(input), "runtime replay transition");
export const resolveTaskResultTransitionForPayloadStatus = (input) => {
    switch (input.payloadStatus) {
        case "success":
            return validateTransition(RUNTIME_TASK_RESULT_TRANSITION_MATRIX.emitSuccessPayload, "pending_result", "runtime task result transition");
        case "partial_success":
            return validateTransition(RUNTIME_TASK_RESULT_TRANSITION_MATRIX.emitPartialSuccessPayload, "pending_result", "runtime task result transition");
        case "failed":
            return validateTransition(RUNTIME_TASK_RESULT_TRANSITION_MATRIX.emitFailedPayload, "pending_result", "runtime task result transition");
        default: {
            const neverStatus = input.payloadStatus;
            throw new Error(`unsupported runtime task result payload status: ${neverStatus}`);
        }
    }
};
export const isRuntimeTaskResultStatusSuccessLike = (status) => isTaskResultStatusSuccessLike(status);
export const resolveRuntimeTaskResultDeliverySemantic = (input) => resolveTaskResultDeliverySemantic({
    payloadStatus: input.payloadStatus,
});
export const resolveCompensationTransition = (input) => {
    if (input.lastAttemptSucceeded) {
        return validateTransition(RUNTIME_COMPENSATION_TRANSITION_MATRIX.settleSuccessfulCompensation, "active", "runtime compensation transition");
    }
    const nextDelayMs = getCompensationDelayMs(input.attemptsCompleted + 1);
    if (nextDelayMs !== null) {
        return validateTransition(RUNTIME_COMPENSATION_TRANSITION_MATRIX.scheduleNextCompensation, "active", "runtime compensation transition");
    }
    return validateTransition(RUNTIME_COMPENSATION_TRANSITION_MATRIX.exhaustFailedCompensation, "active", "runtime compensation transition");
};
